import React from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  insuranceProvider: Yup.string(),
  policyNumber: Yup.string().when('insuranceProvider', {
    is: (val) => Boolean(val),
    then: () =>
      Yup.string().required(
        'Policy number is required when insurance provider is specified'
      ),
    otherwise: () => Yup.string()
  }),
  expiryDate: Yup.date().when('insuranceProvider', {
    is: (val) => Boolean(val),
    then: () =>
      Yup.date()
        .required(
          'Expiry date is required when insurance provider is specified'
        )
        .min(new Date(), 'Expiry date must be in the future')
        .test(
          'is-valid-expiry',
          'Expiry date cannot be more than 10 years in the future',
          (value) => {
            if (!value) return true;
            const tenYearsFromNow = new Date();
            tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);
            return value <= tenYearsFromNow;
          }
        ),
    otherwise: () => Yup.date()
  })
});

const InsuranceInformation = ({
  initialData,
  onComplete,
  onBack,
  optional
}) => {
  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Insurance Information (Optional)</h2>
      <Formik
        initialValues={{
				  insuranceProvider: initialData.insuranceProvider || '',
				  policyNumber: initialData.policyNumber || '',
				  expiryDate: initialData.expiryDate || ''
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
				  onComplete(values, true);
				  setSubmitting(false);
        }}
      >
        {({ errors, touched, isValid, dirty, values }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='insuranceProvider' className={css(styles.label)}>
                Insurance Provider Name
              </label>
              <Field name='insuranceProvider' className={css(styles.input)} />
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='policyNumber' className={css(styles.label)}>
                Policy Number
              </label>
              <Field name='policyNumber' className={css(styles.input)} />
              {errors.policyNumber && touched.policyNumber && (
                <div className={css(styles.error)}>{errors.policyNumber}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='expiryDate' className={css(styles.label)}>
                Expiry Date
              </label>
              <Field
                name='expiryDate'
                type='date'
                className={css(styles.input)}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.expiryDate && touched.expiryDate && (
                <div className={css(styles.error)}>{errors.expiryDate}</div>
              )}
            </div>

            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
								  styles.button,
								  optional && !(isValid && dirty) && styles.disabledButton
                )}
                whileHover={optional && isValid && dirty ? { scale: 1.05 } : {}}
                whileTap={optional && isValid && dirty ? { scale: 0.95 } : {}}
                disabled={optional ? !(isValid && dirty) : false}
              >
                {!optional ? 'Next' : 'Update Profile'}
              </motion.button>
              {!optional
                ? (
                  <motion.button
                    type='button'
                    className={css(styles.button, styles.backButton)}
                    onClick={onBack}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Back
                  </motion.button>
                  )
                : null}
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  },
  nextButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  }
});

export default InsuranceInformation;
